import { RefObject, useEffect, useRef, useState } from 'react';

import { useNow } from '@/hooks/useNow';
import { Nullable } from '@/utils';

/**
 * The element to watch, either directly or behind a ref. Prefer passing the element if available for
 * reactivity. Given a ref, this hook will construct a watcher process for late-arriving elements, but this
 * process is costly and prone to instability.
 */
export type ViewportTarget = Nullable<Element> | RefObject<Nullable<Element>>;

const resolveTarget = (target: ViewportTarget): Nullable<Element> =>
    target && 'current' in target ? target.current : target;

/**
 *
 * @returns true if <em>any</em> of the element is within the viewport
 */
function isCurrentlyInViewport(element: Element | null) {
    if (!element) {
        return false;
    }
    const bounds = element.getBoundingClientRect();
    return (
        bounds.bottom >= 0 &&
        bounds.right >= 0 &&
        bounds.top <= (window.innerHeight ?? document.documentElement.clientHeight) &&
        bounds.left <= (window.innerWidth ?? document.documentElement.clientWidth)
    );
}

const MAX_TARGET_LOOKUP_RETRIES = 10;
const MAX_TARGET_LOOKUP_MS = 10_000;

export const useIsInViewport = (
    target: ViewportTarget,
    { enabled = true, elapsedMs = 0 }: { enabled?: boolean; elapsedMs?: number } = {}
) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasGivenUpOnTarget, setHasGivenUpOnTarget] = useState(false);
    const lookupStartTimeRef = useRef<number | null>(null);
    const retryCountRef = useRef(0);
    const hasLoggedTargetErrorRef = useRef(false);

    /*
        sometimes the ref comes in before the element has shown up and we get null, but since references
        aren't reactive, we never try again. we can't be expected to handle actual changes, but we should
        be able to tolerate delayed initialization. so, if we get an empty reference, keep trying until
        we get the element we were asked to watch.
        callers that pass the element itself don't need any of this: their target is reactive, so we pick it up the moment it arrives.
     */
    const [foundTarget, setFoundTarget] = useState(false);
    const now = useNow(1, { enabled: enabled && !foundTarget && !hasGivenUpOnTarget });

    useEffect(() => {
        if (!enabled) {
            lookupStartTimeRef.current = null;
            retryCountRef.current = 0;
            hasLoggedTargetErrorRef.current = false;
            setHasGivenUpOnTarget(false);
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled || hasGivenUpOnTarget) {
            return;
        }

        let visibilityTimeout: ReturnType<typeof setTimeout> | undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                clearTimeout(visibilityTimeout); // don't stack timers across threshold crossings
                if (!entry.isIntersecting || !elapsedMs) {
                    setIsVisible(entry.isIntersecting);
                } else {
                    visibilityTimeout = setTimeout(() => {
                        setIsVisible(isCurrentlyInViewport(resolveTarget(target)));
                    }, elapsedMs);
                }
            },
            {
                root: null, // viewport
                rootMargin: '0px', // no margin
                threshold: 0.7, // 70% of target visible
            }
        );

        const targetElement = resolveTarget(target);
        if (targetElement) {
            setFoundTarget(true);
            observer.observe(targetElement);
        } else {
            retryCountRef.current += 1;
            const nowMs = Date.now();
            if (lookupStartTimeRef.current === null) {
                lookupStartTimeRef.current = nowMs;
            }

            const retryLimitReached = retryCountRef.current >= MAX_TARGET_LOOKUP_RETRIES;
            const elapsedLimitReached = nowMs - lookupStartTimeRef.current >= MAX_TARGET_LOOKUP_MS;
            if (retryLimitReached || elapsedLimitReached) {
                setHasGivenUpOnTarget(true);

                if (!hasLoggedTargetErrorRef.current) {
                    hasLoggedTargetErrorRef.current = true;
                    console.error('useIsInViewport: target never became present, stopping retries', {
                        retries: retryCountRef.current,
                        elapsedMs: nowMs - lookupStartTimeRef.current,
                    });
                }
            }
        }

        /*
        Both of these matter for memory: a pending timeout holds the element it was going to measure, and
        an observer that is only '.unobserve()'d can still be holding a queued entry for it. Either one keeps a
        target that React has already detached — and its whole ancestor chain — out of reach of the GC.
         */
        return () => {
            clearTimeout(visibilityTimeout);
            observer.disconnect();
        };
    }, [target, now, enabled, elapsedMs, hasGivenUpOnTarget]);

    return isVisible;
};
